import React, { useEffect, useState } from "react";
import Input from "../../../component/common/Input";
import { ReactComponent as SaveIcon } from "../../../assets/save.svg";
import Button from "../../../component/common/Button";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useUser } from "../../../context/UserContext";
import httpServices from "../../../services/httpServices";
import { toast } from "react-toastify";
import Textarea from "../../../component/common/TextArea";

// Define the Yup validation schema
const schema = yup.object().shape({
  name: yup.string().required("Full Name is required"),
  jobTitle: yup.string().nullable(),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  phoneNumber: yup
    .string()
    .matches(/^[0-9]+$/, "Mobile number must be numeric")
    .required("Mobile number is required"),
  description: yup
    .string()
    .min(10, "Description must be at least 10 characters")
    .max(500, "Description can't exceed 500 characters")
    .required("Description is required"),
  businessHubName: yup.string().required("Business Hub Name is required"),
  location: yup.string().required("Location is required"),
});

const ProfileDetails = () => {
  const { user } = useUser();
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      jobTitle: "",
      emailId: "",
      mobile: "",
      description: "",
      businessHubName: "",
      location: "",
    },
  });

  const getUserDetails = async () => {
    try {
      const { response } = await httpServices.get(
        `/users/details/${user?.userId}`,
        {
          skip: !user,
        }
      );
      if (response?.status === 200) {
        console.log(response?.data?.data);
        setUserDetails(response.data?.data);
      }
    } catch (error) {
      toast.error("Failed to fetch user details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      getUserDetails();
    }
  }, [user]);

  useEffect(() => {
    if (userDetails) {
      // Reset form values once user details are fetched
      reset({
        name: userDetails.name,
        jobTitle: userDetails.jobTitle,
        email: userDetails.email,
        phoneNumber: userDetails.phoneNumber,
        description: userDetails.description,
        businessHubName: userDetails.businessHubName,
        location: userDetails.location,
      });
    }
  }, [userDetails, reset]);

  const onSubmit = async (data) => {
    const toastId = toast.loading("Updating profile...");
    try {
      const { response, error } = await httpServices.put(
        `/users/update/${user?.userId}`,
        data
      );

      if (error) {
        throw error;
      }

      toast.update(toastId, {
        render: "Profile updated successfully!",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
    } catch (error) {
      toast.update(toastId, {
        render: error?.response?.data?.message || "Failed to update profile.",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } 
  };

  if (loading) {
    return <div>Loading...</div>;
  }
  return (
    <div className="px-2.5 xs:px-4 mt-5 mb-4">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <Input
                label="Full Name"
                {...register("name")}
                placeholder="Your Full Name"
                inputClasses="bg-[#F5F6FA] pl-3 rounded-[10px] text-[#06152B] placeholder:text-[#71717A]"
                lableClasses="text-sm lg:text-[16px] !text-[#06152B]"
                classes="w-full md:w-[400px]"
              />
              {errors.name && (
                <p className="text-red-500 text-sm">{errors.name.message}</p>
              )}
            </div>
            <div>
              <Input
                label="Job Title"
                {...register("jobTitle")}
                placeholder="Enter Job Title"
                inputClasses="bg-[#F5F6FA] pl-3 rounded-[10px] text-[#06152B] placeholder:text-[#71717A]"
                lableClasses="text-sm lg:text-[16px] !text-[#06152B]"
                classes="w-full md:w-[400px]"
              />
              {errors.jobTitle && (
                <p className="text-red-500 text-sm">
                  {errors.jobTitle.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div>
              <Input
                label="Email"
                {...register("email")}
                placeholder="Your Email ID"
                inputClasses="bg-[#F5F6FA] pl-3 rounded-[10px] text-[#06152B] placeholder:text-[#71717A]"
                lableClasses="text-sm lg:text-[16px] !text-[#06152B]"
                classes="w-full md:w-[400px]"
              />
              {errors.email && (
                <p className="text-red-500 text-sm">{errors.email.message}</p>
              )}
            </div>
            <div>
              <Input
                label="Mobile"
                {...register("phoneNumber")}
                placeholder="Your mobile number"
                inputClasses="bg-[#F5F6FA] pl-3 rounded-[10px] text-[#06152B] placeholder:text-[#71717A]"
                lableClasses="text-sm lg:text-[16px] !text-[#06152B]"
                classes="w-full md:w-[400px]"
                type="number"
              />
              {errors.phoneNumber && (
                <p className="text-red-500 text-sm">
                  {errors.phoneNumber.message}
                </p>
              )}
            </div>
          </div>

          <div className="w-full lg:w-[825px] relative">
            <label className="mb-2 text-sm lg:text-[16px] font-medium text-black block">
              Description
            </label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Textarea
                  placeholder="Put description of up to 50 - 500 Characters"
                  {...field}
                  className="pr-4 pl-5 py-3 text-black rounded bg-[#F5F6FA] w-full placeholder:text-500-gray text-sm focus:outline-none h-[150px] md:h-[77px]"
                />
              )}
            />
            {errors.description && (
              <p className="text-red-500 text-sm">
                {errors.description.message}
              </p>
            )}
          </div>

          <div className="flex items-center flex-wrap gap-4">
            <div>
              <Input
                label="Business Hub Name"
                {...register("businessHubName")}
                placeholder="Your Business Hub Name"
                inputClasses="bg-[#F5F6FA] pl-3 rounded-[10px] text-[#06152B] placeholder:text-[#71717A]"
                lableClasses="text-sm lg:text-[16px] !text-[#06152B]"
                classes="w-full md:w-[400px]"
              />
              {errors.businessHubName && (
                <p className="text-red-500 text-sm">
                  {errors.businessHubName.message}
                </p>
              )}
            </div>
            <div>
              <Input
                label="Location"
                {...register("location")}
                placeholder="Your Location"
                inputClasses="bg-[#F5F6FA] pl-3 rounded-[10px] text-[#06152B] placeholder:text-[#71717A]"
                lableClasses="text-sm lg:text-[16px] !text-[#06152B]"
                classes="w-full md:w-[400px]"
              />
              {errors.location && (
                <p className="text-red-500 text-sm">
                  {errors.location.message}
                </p>
              )}
            </div>
          </div>
        </div>

        <Button
          classes="bg-[#2EDB72] py-3 px-[27px] mt-8"
          Icon={SaveIcon}
          content="Save"
          type="submit"
        />
      </form>
    </div>
  );
};

export default ProfileDetails;
